import { keyBy } from 'lodash/fp';

import { WorkflowVersionMap } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/types';
import {
  fetchInquiryRunData,
  getRunStepConfigs,
} from 'dashboard/apps/InquiryTemplateEditor/utils/inquiry-run';
import {
  fetchInquiryTemplateAndVersion,
  fetchInquiryTemplateVersionSchema,
  fetchInquiryTemplateVersionStepsPreview,
} from 'dashboard/apps/InquiryTemplateEditor/utils/inquiry-template-version';
import { InquiryTemplateVersion } from 'dashboard/models/inquiry-template-version';
import {
  setInquiryRunActors,
  setInquiryRunEvents,
  setInquiryTemplateVersion,
  setRunStepConfigs,
  setStepsPreview,
  setWorkflowVersionMap,
} from 'dashboard/redux/slices/inquiry-run';
import { DashboardThunkAction } from 'dashboard/redux/types';
import { InquiryIso6391LanguageCode, isSupportedInquiryLocale } from 'lib/constants/locales';
import { InquiryRunPermissionMap } from 'lib/hooks/useInquiryRunPermissionMap';
import { StepPreview } from 'lib/interfaces/StepsConfig';
import { Permission } from 'lib/permissions';

async function loadInquiryTemplateVersionData(
  versionId: string,
  locale: InquiryIso6391LanguageCode,
): Promise<[InquiryTemplateVersion, StepPreview[], WorkflowVersionMap]> {
  // default to `en-US` if passed an unsupported locale from the inquiry session
  const supportedLocale: InquiryIso6391LanguageCode = isSupportedInquiryLocale(locale)
    ? locale
    : 'en-US';
  const [{ inquiryTemplateVersion }, stepsPreview, schema] = await Promise.all([
    fetchInquiryTemplateAndVersion({ inquiryTemplateVersionId: versionId }),
    fetchInquiryTemplateVersionStepsPreview({
      inquiryTemplateVersionId: versionId,
      locale: supportedLocale,
    }),
    fetchInquiryTemplateVersionSchema({ inquiryTemplateVersionId: versionId }),
  ]);
  return [inquiryTemplateVersion, stepsPreview, keyBy('id', schema['workflow-versions'].data)];
}

export const loadInquiryRunData =
  ({
    inquiryId,
    inquiryTemplateVersionId,
    locale,
    environmentId,
    inquiryRunPermissionMap,
  }: {
    inquiryId: string;
    inquiryTemplateVersionId: string;
    locale: InquiryIso6391LanguageCode;
    environmentId: string;
    inquiryRunPermissionMap: InquiryRunPermissionMap;
  }): DashboardThunkAction =>
  async (dispatch) => {
    const { events, actors } = await fetchInquiryRunData({
      inquiryId,
      environmentId,
      inquiryRunPermissionMap,
    });

    dispatch(setInquiryRunActors({ inquiryRunActors: actors }));
    dispatch(setInquiryRunEvents({ inquiryRunEvents: events }));

    if (inquiryRunPermissionMap[Permission.InquiryTemplateView]) {
      const [inquiryTemplateVersion, stepsPreview, workflowVersionMap] =
        await loadInquiryTemplateVersionData(inquiryTemplateVersionId, locale);
      dispatch(setInquiryTemplateVersion({ inquiryTemplateVersion }));
      dispatch(setStepsPreview({ stepsPreview }));
      dispatch(setWorkflowVersionMap({ workflowVersionMap }));
      dispatch(
        setRunStepConfigs({
          runStepConfigs: getRunStepConfigs(events, inquiryTemplateVersion.attributes.steps),
        }),
      );
    }
  };
